import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Landmark, Scale, Skull, Heart, ShieldCheck, Target, Zap, Circle } from 'lucide-react';
import { StoicRule } from '../types';

const RULES: StoicRule[] = [
  { id: 1, title: "Live with Areté", concept: "Virtue", description: "Areté means 'excellence of character'. It is the highest good. To live with Areté is to close the gap between who you are capable of being and who you are being right now.", icon: "ShieldCheck" },
  { id: 2, title: "The Dichotomy of Control", concept: "Focus", description: "Some things are up to us (our judgments, actions, desires), and some are not (body, wealth, reputation). Happiness comes from focusing only on what is up to us.", icon: "Scale" },
  { id: 3, title: "Take Responsibility", concept: "Freedom", description: "You are not disturbed by events, but by your views about them. Take full responsibility for your mind. No one can harm your character without your consent.", icon: "Target" },
  { id: 4, title: "Memento Mori", concept: "Mortality", description: "Remember you will die. This is not morbid; it is a tool to create priority and meaning. Stop wasting time on trivialities.", icon: "Skull" },
  { id: 5, title: "Amor Fati", concept: "Acceptance", description: "Love your fate. Don't just bear it; love it. Treat every event as if you had chosen it yourself. The obstacle is the way.", icon: "Heart" },
  { id: 6, title: "Premeditatio Malorum", concept: "Resilience", description: "Negative visualization. Anticipate what could go wrong so you are not crushed by surprise. Rehearse your response to adversity.", icon: "Zap" },
  { id: 7, title: "The View from Above", concept: "Perspective", description: "Zoom out. See your life from the perspective of the cosmos. Your problems are small, and you are part of a greater whole.", icon: "Circle" },
  { id: 8, title: "Eudaimonia", concept: "Flourishing", description: "The goal of life is not pleasure, but 'good spirit' or flourishing. This state is achieved by living in agreement with nature and virtue.", icon: "Landmark" }
];

const IconMap: Record<string, React.ReactNode> = {
  Landmark: <Landmark size={24} />,
  Scale: <Scale size={24} />,
  Skull: <Skull size={24} />,
  Heart: <Heart size={24} />,
  ShieldCheck: <ShieldCheck size={24} />,
  Target: <Target size={24} />,
  Zap: <Zap size={24} />,
  Circle: <Circle size={24} />,
};

const Stoic: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-stone-100 text-stone-600 rounded-full mb-4">
          <Landmark size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">The Little Book of Stoicism</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Areté: Excellence of Character"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          Inspired by Jonas Salzgeber, Stoicism is a practical philosophy for life. 
          It teaches us to focus on what we can control and accept what we cannot with equanimity.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-stone-400 shadow-md' : 'border-nunchi-200 hover:border-stone-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-stone-100 text-stone-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif uppercase tracking-wider">{rule.concept}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-stone-200 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Stoic;